import { useSyncExternalStore } from 'react';

/**
 * Follow-up realtime notification state (shared by the sidebar, mobile nav and Follow-ups page):
 * - count: unseen `followup.updated` events since the user last opened the Follow-ups page
 *   (sidebar badge). Cleared when the page is opened / the sidebar item is clicked.
 * - unseen tabs: tabs (due | upcoming | completed | trash) that received a follow-up the user hasn't
 *   looked at yet (green dot on the tab). Cleared when that tab is opened.
 * - unseen follow-ups: ids of follow-ups that arrived through realtime and haven't been opened yet
 *   (green dot on the card). Cleared when the card is opened.
 * Persisted so it survives a reload, and synced across browser tabs via the `storage` event.
 */
const COUNT_KEY = 'follow_up_due_badge_count';
const TABS_KEY = 'follow_up_unseen_tabs';
const ITEMS_KEY = 'follow_up_unseen_items';
// Keep only the most recent ids (oldest dropped first)
const MAX_UNSEEN_ITEMS = 200;
const listeners = new Set();

const readCount = () => {
  try {
    const value = parseInt(localStorage.getItem(COUNT_KEY), 10);
    return Number.isNaN(value) || value < 0 ? 0 : value;
  } catch {
    return 0;
  }
};

const readTabs = () => {
  try {
    const value = JSON.parse(localStorage.getItem(TABS_KEY) || '[]');
    return Array.isArray(value) ? value.filter((tab) => typeof tab === 'string') : [];
  } catch {
    return [];
  }
};

const readItems = () => {
  try {
    const value = JSON.parse(localStorage.getItem(ITEMS_KEY) || '[]');
    return Array.isArray(value) ? value.map(String) : [];
  } catch {
    return [];
  }
};

let count = readCount();
let unseenTabs = readTabs();
let unseenItems = readItems();
// Tab / follow-up currently on screen on the Follow-ups page (null when closed) — not persisted
let visibleTab = null;
let openFollowUpId = null;

const notify = () => listeners.forEach((listener) => listener());

const persist = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage errors (private browsing)
  }
};

const setCount = (next) => {
  if (next === count) return;
  count = next;
  persist(COUNT_KEY, String(next));
  notify();
};

const setUnseenTabs = (next) => {
  if (next.length === unseenTabs.length && next.every((tab) => unseenTabs.includes(tab))) return;
  unseenTabs = next;
  persist(TABS_KEY, JSON.stringify(next));
  notify();
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === COUNT_KEY) count = readCount();
    else if (event.key === TABS_KEY) unseenTabs = readTabs();
    else if (event.key === ITEMS_KEY) unseenItems = readItems();
    else return;
    notify();
  });
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const incrementFollowUpDueBadge = () => setCount(count + 1);
export const clearFollowUpDueBadge = () => setCount(0);

/** Mark a tab as having unseen follow-ups — skipped when the user is looking at that tab. */
export const markFollowUpTabUnseen = (tab) => {
  if (!tab || tab === visibleTab || unseenTabs.includes(tab)) return;
  setUnseenTabs([...unseenTabs, tab]);
};

export const clearFollowUpTabUnseen = (tab) => {
  if (unseenTabs.includes(tab)) setUnseenTabs(unseenTabs.filter((item) => item !== tab));
};

/** Follow-ups page reports the tab on screen (null when it closes); that tab's dot is cleared. */
export const setVisibleFollowUpTab = (tab) => {
  visibleTab = tab;
  if (tab) clearFollowUpTabUnseen(tab);
};

const setUnseenItems = (next) => {
  unseenItems = next;
  persist(ITEMS_KEY, JSON.stringify(next));
  notify();
};

/** A follow-up arrived / changed through realtime — show its "new" dot until it's opened. */
export const markFollowUpUnseen = (followUpId) => {
  if (followUpId == null) return;
  const id = String(followUpId);
  if (id === openFollowUpId) return; // the user is looking at it
  const next = [...unseenItems.filter((item) => item !== id), id];
  setUnseenItems(next.length > MAX_UNSEEN_ITEMS ? next.slice(next.length - MAX_UNSEEN_ITEMS) : next);
};

export const clearFollowUpUnseen = (followUpId) => {
  const id = String(followUpId);
  if (unseenItems.includes(id)) setUnseenItems(unseenItems.filter((item) => item !== id));
};

/** Follow-ups page reports the follow-up open in the details panel (null when none). */
export const setOpenFollowUp = (followUpId) => {
  openFollowUpId = followUpId == null ? null : String(followUpId);
  if (openFollowUpId) clearFollowUpUnseen(openFollowUpId);
};

const getCount = () => count;
const getUnseenItems = () => unseenItems;
const getUnseenTabs = () => unseenTabs;

/** Sidebar badge count (re-renders on change). */
export const useFollowUpDueBadge = () => useSyncExternalStore(subscribe, getCount, getCount);

/** Tabs with unseen follow-ups (array of tab keys). */
export const useFollowUpUnseenTabs = () => useSyncExternalStore(subscribe, getUnseenTabs, getUnseenTabs);

/** Ids (strings) of follow-ups with unseen realtime updates. */
export const useFollowUpUnseenItems = () => useSyncExternalStore(subscribe, getUnseenItems, getUnseenItems);
